import { f7 } from 'framework7-react';
import II18 from '../../translate/II18';
import { gae } from '../../helpers/Analytics';
import { EXTERNAL_POPUP_WINDOW_STATUS } from '../../../redux/actions/rootAction';

const POLL_START_DELAY_MS = 30000;
const POLL_PERIOD_MS = 5000;

/**
 * Shared signature-status watcher (delayed cron + WS topic).
 * Provider owners (A-Trust / SignMe modals, YES path in HOC) call start/pause;
 * getSignatureStatus / wsTopic come from the provider adapter.
 *
 * Cron may continue after the provider popup closes — callers that keep a
 * status-check UI (e.g. SignMe) rely on that; callers that pause() on close
 * are unchanged.
 */
export function createSignatureStatusWatch({
  startCronJob,
  stopCronJob,
  subscribeWebSocket,
  unSubscribeWebSocket,
  getExternalPopUpStatus,
  receiveESignatureData,
  onProcessFailed,
  usePreloaderDialog = false,
  onPreloaderCancel
}) {
  let aborted = false;
  let timer = null;
  let sid = null;
  let wsTopic = null;
  let provider = null;
  let cronActive = false;

  const clearTimer = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const stopCronAndWs = () => {
    if (sid) {
      stopCronJob(sid);
    }
    if (wsTopic) {
      unSubscribeWebSocket(wsTopic);
    }
    cronActive = false;
  };

  const pause = () => {
    aborted = true;
    clearTimer();
    if (usePreloaderDialog) {
      f7.dialog.close();
    }
    stopCronAndWs();
  };

  const runStatusPoll = timeIsOut => {
    if (aborted) {
      stopCronJob(sid);
      return;
    }
    if (timeIsOut) {
      gae('ESIGN_PROCESS', 'STOP_SIGNATURE_CHECKING_CRONJOB', 'TIME_IS_OUT');
      stopCronJob(sid);
      if (wsTopic) {
        unSubscribeWebSocket(wsTopic);
      }
      cronActive = false;
      return;
    }

    // Return Promise so cron poll-quiet stays active until XHR settles
    return provider?.getSignatureStatus(sid)?.then(
      response => {
        gae('ESIGN_PROCESS', 'RECEIVE_PROCESS_STATE', response.processState);

        if (response.processState === 'OK') {
          receiveESignatureData(response);
        }

        if (
          response.processState === 'FAILED' ||
          response.processState === 'CANCEL'
        ) {
          onProcessFailed?.();
        }

        if (
          !(
            response.processState === 'CREATED' ||
            response.processState === 'SIGNING_IN_PROGRESS'
          )
        ) {
          stopCronJob(sid);
          if (wsTopic) {
            unSubscribeWebSocket(wsTopic);
          }
          cronActive = false;
        }
      },
      () => {
        // ignore transient status errors
      }
    );
  };

  const beginCron = () => {
    if (aborted || !sid || !provider || cronActive) {
      return;
    }
    cronActive = true;

    startCronJob(sid, POLL_PERIOD_MS, runStatusPoll);
    // setInterval waits one full period before the first tick.
    runStatusPoll();
  };

  /**
   * Start (or keep) cron immediately — used when entering status-check UI after
   * the provider window closed before the delayed poll kicked in.
   */
  const ensurePolling = () => {
    if (aborted || !sid) {
      return;
    }
    clearTimer();
    if (getExternalPopUpStatus() !== EXTERNAL_POPUP_WINDOW_STATUS.OPEN) {
      gae(
        'ESIGN_PROCESS',
        'START_SIGNATURE_CHECKING_CRONJOB',
        'ENSURE_AFTER_POPUP_CLOSED'
      );
    }
    beginCron();
  };

  const start = (
    internalCallbackId,
    nextProvider,
    pollStartDelayMs = POLL_START_DELAY_MS
  ) => {
    // Fresh cycle (e.g. START retry after user closed popup)
    pause();
    aborted = false;
    cronActive = false;
    sid = internalCallbackId;
    provider = nextProvider || null;
    wsTopic = nextProvider?.wsTopic || null;

    gae('ESIGN_PROCESS', 'START_SIGNATURE_CHECKING_CRONJOB');

    if (usePreloaderDialog) {
      f7.dialog.preloader(II18.get('dialog_processing'), '', () => {
        pause();
        onPreloaderCancel?.();
      });
    }

    if (wsTopic) {
      subscribeWebSocket(wsTopic, sid);
    }

    timer = setTimeout(() => {
      timer = null;
      if (aborted) {
        gae(
          'ESIGN_PROCESS',
          'START_SIGNATURE_CHECKING_CRONJOB',
          'SKIP_ABORTED'
        );
        return;
      }
      // Do not require popup OPEN — status-check UX keeps waiting after close.
      if (getExternalPopUpStatus() !== EXTERNAL_POPUP_WINDOW_STATUS.OPEN) {
        gae(
          'ESIGN_PROCESS',
          'START_SIGNATURE_CHECKING_CRONJOB',
          'POPUP_CLOSED_START_POLL'
        );
      }
      beginCron();
    }, pollStartDelayMs);
  };

  return { start, pause, stop: pause, ensurePolling };
}
